import LanguageSelector from "../../components/common/LanguageSelector";
import { useTranslation } from "react-i18next";
import { apiUrl } from "../../config/environment";
import { useState, type FormEvent } from "react";
import { Save, ShieldCheck, Upload } from "lucide-react";
import { useAuth } from "../../store/AuthContext";
import { Avatar, Badge, ErrorNotice } from "../../components/common/Primitives";
import { api, uploadFile } from "../../services/api";
import type { User } from "../../config/types";
import { roleLabel } from "../../config/constants/features";
export default function ProfilePage() {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [photoName, setPhotoName] = useState("");
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body: Record<string, unknown> = {
      firstName: data.get("firstName"),
      lastName: data.get("lastName"),
      email: data.get("email"),
    };
    if (data.get("password")) {
      body.password = data.get("password");
      body.currentPassword = data.get("currentPassword");
    }
    if (body.password && body.password !== data.get("confirmPassword")) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const file = data.get("photo");
      if (file instanceof File && file.size)
        body.attachmentId = (await uploadFile(file, "profile")).id;
      const result = await api<User>("/auth/me", {
        method: "PATCH",
        body: JSON.stringify(body),
      });
      if (body.password) {
        setUser(null);
      } else {
        setUser(result);
        setMessage("Your profile has been updated.");
        form.reset();
        setPhotoName("");
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <div className="page-heading">
        <div>
          <div className="eyebrow">{t("MAKE YOURSELF AT HOME")}</div>
          <h1>{t("My profile")}</h1>
          <p>{t("Your personal details and account security.")}</p>
        </div>
      </div>
      <section className="panel language-panel">
        <div>
          <h2>{t("Language preference")}</h2>
          <p>
            {t("Your browser language is used by default. Choose English or French for this device.")}
          </p>
        </div>
        <LanguageSelector />
      </section>
      <div className="profile-grid">
        <section className="panel profile-card">
          <Avatar name={user!.firstName + " " + user!.lastName} src={user!.googlePhotoUrl || (user!.attachmentId ? apiUrl("/files/" + user!.attachmentId) : undefined)} />
          <h2>
            {user!.firstName} {user!.lastName}
          </h2>
          <p>{user!.email}</p>
          <Badge>{t(roleLabel[user!.role] || user!.role)}</Badge>
          <div className="profile-tip">
            <ShieldCheck size={22} />
            <p>
              {t("Your access is managed by your workspace administrator.")}
            </p>
          </div>
        </section>
        <section className="panel">
          <div className="panel-heading">
            <div>
              <h2>{t("Personal information")}</h2>
              <p>{t("Keep your teammates up to date.")}</p>
            </div>
          </div>
          <form onSubmit={submit}>
            <div className="form-body">
              {error && <ErrorNotice message={error} />}{" "}
              {message && (
                <div className="success-message" role="status">
                  {t(message)}
                </div>
              )}
              <div className="form-grid">
                <label>
                  {t("First name")}
                  <input
                    name="firstName"
                    required
                    defaultValue={user!.firstName}
                  />
                </label>
                <label>
                  {t("Last name")}
                  <input
                    name="lastName"
                    required
                    defaultValue={user!.lastName}
                  />
                </label>
                <label className="span-two">
                  {t("Email address")}
                  <input
                    name="email"
                    type="email"
                    defaultValue={user!.email}
                    required
                  />
                </label>
                <label className="span-two">
                  {t("Profile attachment")}
                  <input
                    className="file-picker-input"
                    type="file"
                    name="photo"
                    accept=".jpg,.jpeg,.png,.webp"
                    onChange={(event) =>
                      setPhotoName(event.target.files?.[0]?.name || "")
                    }
                  />
                  <span className="file-picker-control">
                    <span className="file-picker-button">
                      <Upload size={16} />
                      {t("Choose file")}
                    </span>
                    <span className="file-picker-name">
                      {photoName || t("No file selected")}
                    </span>
                  </span>
                  {user!.attachmentId && (
                    <a href={apiUrl("/files/" + user!.attachmentId)}>
                      {t("Download current photo")}
                    </a>
                  )}
                </label>
              </div>
              <div className="form-section-title security-title">
                {t("CHANGE PASSWORD")}
              </div>
              <p className="muted">
                {t(
                  "Leave these fields empty to keep your current password. Changing it signs you out of all sessions.",
                )}
              </p>
              <div className="form-grid">
                <label>
                  {t("Current password")}
                  <input
                    name="currentPassword"
                    type="password"
                    autoComplete="current-password"
                  />
                </label>
                <label>
                  {t("New password")}
                  <input
                    name="password"
                    type="password"
                    minLength={12}
                    maxLength={128}
                    autoComplete="new-password"
                    placeholder={t("At least 12 characters")}
                  />
                </label>
                <label>
                  {t("Confirm password")}
                  <input
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                  />
                </label>
              </div>
            </div>
            <div className="modal-actions">
              <button className="button primary" disabled={busy}>
                <Save size={17} />
                {busy ? t("Saving…") : t("Save changes")}
              </button>
            </div>
          </form>
        </section>
      </div>
    </>
  );
}
